import { swSettings } from "app/common/services/swSettings";
import type { BasicDurationValues } from "app/services/DurationService";
import { BasicDurations, type IDurationData } from "app/services/DurationService";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import type { ISwitcherItem } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";

// Duration helpers
const GraphDurationToAmountAndUnit: Record<string, [number, string]> = {
    "28d": [28, "days"],
    "1m": [1, "months"],
    "3m": [3, "months"],
    "6m": [6, "months"],
    "13m": [13, "months"],
};

const checkDurationLimit = (duration: string): boolean => {
    const count = swSettings.current.resources.SupportedInterval.count;
    const [amount, unit] = GraphDurationToAmountAndUnit[duration];
    return unit === "months" && amount > count;
};

const checkDurationStatus = (duration: string, isLockedCheck: boolean): boolean => {
    const isLimitedDuration = checkDurationLimit(duration);
    if (isLimitedDuration) {
        return isLockedCheck ? swSettings.current.durationPaymentHook : !swSettings.current.durationPaymentHook;
    }
    return false;
};

export const isDurationSwitcherLocked = (duration: string): boolean => checkDurationStatus(duration, true);
export const isDurationSwitcherDisabled = (duration: string): boolean => checkDurationStatus(duration, false);

// Granularity helpers
export const getUpdatedGranularitySwitcherItems = (
    graphDuration: string,
    switcherOptions: ISwitcherItem[],
): ISwitcherItem[] =>
    switcherOptions.map((item) => {
        let disabled = false;
        if (item.value === GranularityNames.MONTHLY) {
            disabled =
                graphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS || graphDuration === BasicDurations.LAST_MONTH;
        }
        if (item.value === GranularityNames.DAILY || item.value === GranularityNames.WEEKLY) {
            disabled = graphDuration === BasicDurations.LAST_THIRTEEN_MONTHS;
        }
        return { ...item, disabled } as ISwitcherItem;
    });

export const resolveGraphGranularityValue = (
    graphDuration: BasicDurationValues,
    graphGranularity: GranularityNames,
): GranularityNames | undefined => {
    const isMonthlyGranularitySelected = graphGranularity === GranularityNames.MONTHLY;
    const isDailyGranularitySelected = graphGranularity === GranularityNames.DAILY;
    const isWeeklyGranularitySelected = graphGranularity === GranularityNames.WEEKLY;
    const isLast28DaysDurationSelected = graphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const isLastMonthDurationSelected = graphDuration === BasicDurations.LAST_MONTH;
    const isLast13MonthsDurationSelected = graphDuration === BasicDurations.LAST_THIRTEEN_MONTHS;

    if ((isLast28DaysDurationSelected || isLastMonthDurationSelected) && isMonthlyGranularitySelected) {
        return GranularityNames.WEEKLY;
    }
    if (isLast13MonthsDurationSelected && (isDailyGranularitySelected || isWeeklyGranularitySelected)) {
        return GranularityNames.MONTHLY;
    }
    return undefined;
};

export const updateGraphGranularityValue = (
    graphDuration: BasicDurationValues,
    graphGranularity: GranularityNames,
    updateKey: string,
): Record<string, GranularityNames> | undefined => {
    const resolvedValue = resolveGraphGranularityValue(graphDuration, graphGranularity);
    if (resolvedValue) {
        return {
            [updateKey]: resolvedValue,
        } as Record<string, GranularityNames>;
    }
    return undefined;
};

// Partial week helper
export const hasPartialWeek = (graphGranularity: string, isLast28: boolean, durationObj: IDurationData): boolean => {
    const hasMonthlySelectedOnSnapshot = graphGranularity === GranularityNames.MONTHLY && !isLast28;
    const isSunday = durationObj.raw.from.day() === 0;
    const hasDailySelected = graphGranularity === GranularityNames.DAILY;
    return !isSunday && !hasDailySelected && !hasMonthlySelectedOnSnapshot;
};
